import moment from 'moment';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { ICS2ArrivalNotificationMessage } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getIcs2NotificationMessageLabel } from 'store/declarations/eu/ics2/ics2-notification';
import useICS2Codelists from 'hooks/useICS2Codelists';
import { readDateTime, readText } from '../arrivalMessageUtils';
import DetailsHeader from 'views/declarations/common/ics2-message-details/DetailsHeader';
import MessageDetails from 'views/declarations/common/ics2-message-details/MessageDetails';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

type Props = {
    message: ICS2ArrivalNotificationMessage;
    onBack: () => void;
};

/**
 * The registration of the arrival.
 *
 * This is the only message carrying both references: the LRN we submitted under and the MRN customs
 * assigned. Every later message about this arrival - a house state problem, a control - is keyed by
 * that MRN alone, so it is worth showing plainly.
 */
const IE3R04Details = ({ message, onBack }: Props) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    const office = readText(message, ['customsOfficeOfFirstEntry', 'referenceNumber']);
    const registeredAt = readDateTime(message, ['registrationDate']);
    const messageTypeLabel = getIcs2NotificationMessageLabel(message.messageType);

    const metadata: Record<string, string> = {
        ...(message.messageType
            ? { 'Message Type': `${message.messageType}${messageTypeLabel ? ` - ${messageTypeLabel}` : ''}` }
            : {}),
        ...(readText(message, ['MRN']) ? { MRN: readText(message, ['MRN'])! } : {}),
        ...(readText(message, ['LRN']) ? { LRN: readText(message, ['LRN'])! } : {}),
        ...(registeredAt ? { Registered: moment(registeredAt).format(DATE_FORMAT) } : {}),
        ...(office
            ? { 'Customs Office of First Entry': getICS2CodeDescription(office, ICS2CodelistsMap.CL141) || office }
            : {}),
        Received: moment(message.receivedAt).format(DATE_FORMAT),
    };

    return (
        <>
            <DetailsHeader onBack={onBack} />

            <MessageDetails
                messageMetadata={metadata}
                details={{
                    whatHappened:
                        'Customs accepted the arrival notification and registered it. The goods are recorded as having arrived at the customs office of first entry.',
                    nextSteps: (
                        <ul style={{ marginBottom: 0, listStyleType: 'initial' }}>
                            <li>Keep the MRN - customs identifies this arrival by it from now on.</li>
                            <li>
                                Registration does not mean the goods are released, and it does not mean every referenced
                                consignment passed its own checks.
                            </li>
                            <li>
                                Customs may still send a control notification or report a house consignment in the wrong
                                state. Both appear here when they arrive.
                            </li>
                        </ul>
                    ),
                }}
            />
        </>
    );
};

export default IE3R04Details;
